import { PALETTE, rampStep } from '../styles/palette';

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Подпись для доступности: график всегда имеет текстовый эквивалент. */
  label: string;
}

/**
 * Чистый SVG, без библиотек. Одиночная линия — только тёмный шаг рампы
 * (светлые зелёные годятся для заливок, не для штрихов).
 */
export function Sparkline({ values, width = 120, height = 32, label }: SparklineProps) {
  if (values.length < 2) return null;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const stepX = width / (values.length - 1);

  const points = values
    .map((v, i) => `${(i * stepX).toFixed(2)},${(height - ((v - min) / span) * height).toFixed(2)}`)
    .join(' ');

  return (
    <svg
      className="spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <line className="spark__base" x1={0} y1={height} x2={width} y2={height} />
      <polyline
        points={points}
        fill="none"
        stroke={PALETTE.active}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface MiniBarProps {
  values: number[];
  /** Подписи столбцов — для доступного эквивалента. */
  names?: string[];
  width?: number;
  height?: number;
  label: string;
}

/** Столбики зелёной рампой: выше значение — темнее шаг. */
export function MiniBar({ values, names, width = 160, height = 40, label }: MiniBarProps) {
  if (values.length === 0) return null;

  const max = Math.max(...values, 1);
  const gap = 2;
  const barW = Math.max(2, (width - gap * (values.length - 1)) / values.length);

  return (
    <svg
      className="spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      {values.map((v, i) => {
        const h = Math.max(1, (v / max) * height);
        const fill = rampStep(v / max);
        return (
          <rect
            key={i}
            className="minibar__cell"
            x={i * (barW + gap)}
            y={height - h}
            width={barW}
            height={h}
            rx={2}
            fill={fill}
            stroke={PALETTE.active}
            strokeWidth={0.5}
          >
            {names?.[i] ? <title>{`${names[i]}: ${v}`}</title> : null}
          </rect>
        );
      })}
    </svg>
  );
}
